import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Lock, Video, Award } from "lucide-react";
import { toast } from "sonner";

// ─── Legacy360 125 Plan Certification Gate ─────────────────────────────────────
// Separate from the main Legacy360/Living Trust certification — Living Trust
// access is entirely unaffected by this component. Video 1 must finish before
// Video 2 unlocks; Video 2 must finish before the quiz unlocks; passing the
// quiz (8/10) unlocks the 125 Plan referral link and resources below. All
// three gates are re-verified server-side (see legacy360Plan125Router) — this
// component only reflects that persisted state, it never grants access itself.
//
// Relocated 2026-08-04 from RepDashboard.tsx so it can be reached from the
// Certifications page (as its own list entry) in addition to being referenced
// from the Referral Links page. Logic/markup unchanged from the original.
export function Legacy360Plan125CertGate({ repCode }: { repCode: string }) {
  const statusQuery = trpc.legacy360Plan125.getStatus.useQuery({ repCode });
  const videoUrlsQuery = trpc.legacy360Plan125.getVideoUrls.useQuery();
  const quizQuery = trpc.legacy360Plan125.getQuiz.useQuery(
    { repCode },
    { enabled: !!statusQuery.data?.video2WatchedAt && !statusQuery.data?.passed }
  );
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<any>(null);

  const markWatched = trpc.legacy360Plan125.markVideoWatched.useMutation({
    onSuccess: () => statusQuery.refetch(),
  });
  const submitQuiz = trpc.legacy360Plan125.submitQuiz.useMutation({
    onSuccess: (data) => {
      setResult(data);
      statusQuery.refetch();
      if (data.passed) toast.success("🎉 Legacy360 125 Plan certification passed!");
      else toast.error(`You scored ${data.score}/${data.total}. You need ${data.passingScore} to pass.`);
    },
    onError: (e) => toast.error(e.message),
  });

  if (statusQuery.isLoading) return null;
  const status = statusQuery.data;
  if (status?.passed) {
    return (
      <div className="bg-[#EFF4E2] border border-[#84ab4a]/25 rounded-2xl p-4 mb-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-[#5F8A2E] flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-[#3F5C1F]">Legacy360 125 Plan — Certified</p>
          <p className="text-xs text-[#6E8A4A]">Your 125 Plan referral link and resources below are unlocked.</p>
        </div>
      </div>
    );
  }

  const video1Done = !!status?.video1WatchedAt;
  const video2Done = !!status?.video2WatchedAt;
  const video1Url = videoUrlsQuery.data?.video1Url;
  const video2Url = videoUrlsQuery.data?.video2Url;

  return (
    <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-[#C8920A]" />
        <p className="text-sm font-bold text-[#2C2723]">Legacy360 125 Plan Certification Required</p>
      </div>
      <p className="text-xs text-[#9C9285] mb-4">
        Your 125 Plan referral link and resources are locked until you watch both training videos and pass the 10-question certification quiz (8/10 required).
      </p>

      {/* Video 1 & Video 2 — side by side so each player gets real room */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
        {/* Video 1 */}
        <div className="border border-[#F0E8DE] rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            {video1Done ? <CheckCircle2 className="w-4 h-4 text-[#5F8A2E] flex-shrink-0" /> : <Video className="w-4 h-4 text-[#C8920A] flex-shrink-0" />}
            <span className="text-sm font-semibold text-[#2C2723] flex-1">Video 1</span>
            {video1Done && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EFF4E2] text-[#5F8A2E]">Watched</span>}
          </div>
          {video1Url ? (
            <video
              src={video1Url}
              controls
              className="w-full aspect-video rounded-lg bg-black"
              onEnded={() => markWatched.mutate({ repCode, video: "video1" })}
            />
          ) : (
            <div className="w-full aspect-video rounded-lg bg-[#FBF7F0] border border-dashed border-[#EBE2D4] flex items-center justify-center">
              <span className="text-xs text-[#9C9285]">Video not yet available — check back soon</span>
            </div>
          )}
        </div>

        {/* Video 2 */}
        <div className={`border border-[#F0E8DE] rounded-xl p-3 ${!video1Done ? "opacity-40" : ""}`}>
          <div className="flex items-center gap-2 mb-2">
            {video2Done ? <CheckCircle2 className="w-4 h-4 text-[#5F8A2E] flex-shrink-0" /> : <Video className="w-4 h-4 text-[#C8920A] flex-shrink-0" />}
            <span className="text-sm font-semibold text-[#2C2723] flex-1">Video 2</span>
            {video2Done && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EFF4E2] text-[#5F8A2E]">Watched</span>}
          </div>
          {!video1Done ? (
            <div className="w-full aspect-video rounded-lg bg-[#FBF7F0] border border-dashed border-[#EBE2D4] flex items-center justify-center">
              <span className="text-xs text-[#9C9285]">Complete Video 1 first</span>
            </div>
          ) : video2Url ? (
            <video
              src={video2Url}
              controls
              className="w-full aspect-video rounded-lg bg-black"
              onEnded={() => markWatched.mutate({ repCode, video: "video2" })}
            />
          ) : (
            <div className="w-full aspect-video rounded-lg bg-[#FBF7F0] border border-dashed border-[#EBE2D4] flex items-center justify-center">
              <span className="text-xs text-[#9C9285]">Video not yet available — check back soon</span>
            </div>
          )}
        </div>
      </div>

      {/* Quiz */}
      <div className={`border border-[#F0E8DE] rounded-xl p-3 ${!video2Done ? "opacity-40" : ""}`}>
        <div className="flex items-center gap-3 mb-1">
          <Award className="w-4 h-4 text-[#C8920A] flex-shrink-0" />
          <span className="text-sm font-medium text-[#2C2723] flex-1">Certification Quiz (10 questions, 8 required to pass)</span>
        </div>
        {!video2Done ? (
          <p className="text-[11px] text-[#9C9285] pl-7">Complete both videos to unlock the quiz</p>
        ) : result ? (
          <div className="pl-7">
            <p className={`text-xs font-semibold ${result.passed ? "text-[#5F8A2E]" : "text-[#C0392B]"}`}>
              Scored {result.score}/{result.total} — {result.passed ? "Passed!" : "Not quite — try again"}
            </p>
            {!result.passed && (
              <Button size="sm" className="mt-2 bg-[#C8920A] hover:bg-[#a97908] text-white border-0" onClick={() => { setResult(null); setAnswers({}); }}>
                Retake Quiz
              </Button>
            )}
          </div>
        ) : quizQuery.data ? (
          <div className="pl-7 space-y-4">
            {quizQuery.data.questions.map((q: any, idx: number) => (
              <div key={q.id}>
                <p className="text-xs font-semibold text-[#2C2723] mb-1.5">Q{idx + 1}. {q.question}</p>
                <div className="space-y-1">
                  {q.options.map((opt: string) => (
                    <label key={opt} className="flex items-center gap-2 text-xs text-[#4A4238] cursor-pointer">
                      <input
                        type="radio"
                        name={q.id}
                        checked={answers[q.id] === opt}
                        onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <Button
              size="sm"
              disabled={submitQuiz.isPending || Object.keys(answers).length < quizQuery.data.questions.length}
              className="bg-[#C8920A] hover:bg-[#a97908] text-white border-0"
              onClick={() => submitQuiz.mutate({ repCode, answers })}
            >
              {submitQuiz.isPending ? "Submitting…" : "Submit Quiz"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Referral Links page notice ─────────────────────────────────────────────
// Compact replacement for the embedded video/quiz gate that used to sit on the
// Referral Links page. Reuses the same status query as the full gate above
// (same source of truth, no duplicated certification state). Uncertified
// associates are pointed to the Certifications page to start the real
// experience there; certified associates see the same "unlocked" notice as
// before so their referral link view is unchanged.
export function Legacy360Plan125ReferralLinkNotice({ repCode }: { repCode: string }) {
  const statusQuery = trpc.legacy360Plan125.getStatus.useQuery({ repCode });
  if (statusQuery.isLoading) return null;
  if (statusQuery.data?.passed) {
    return (
      <div className="bg-[#EFF4E2] border border-[#84ab4a]/25 rounded-2xl p-4 mb-4 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-[#5F8A2E] flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-[#3F5C1F]">Legacy360 125 Plan — Certified</p>
          <p className="text-xs text-[#6E8A4A]">Your 125 Plan referral link and resources below are unlocked.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#EBE2D4] rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Lock className="w-4 h-4 text-[#C8920A]" />
        <p className="text-sm font-bold text-[#2C2723]">Certification Required</p>
      </div>
      <p className="text-xs text-[#9C9285]">
        Complete the Legacy360 125 Plan Certification to unlock your referral link. Go to <strong className="text-[#6E665A]">Certifications</strong> in the sidebar to get started.
      </p>
    </div>
  );
}
